import { format, isValid, parse } from 'date-fns'
import { Calendar as CalendarIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Calendar, Input } from '@/components'
import { parseCalendarDate, toCalendarDate } from '@/utils'
import { FilterInputWrapper } from './FilterInputWrapper'
import type { DateInputProps } from './types'

const DATE_FORMAT = 'MM/dd/yyyy'

function formatWithMask(digits: string): string {
  let result = ''
  for (let index = 0; index < digits.length && index < 8; index++) {
    if (index === 2 || index === 4) result += '/'
    result += digits[index]
  }
  if (digits.length === 2 || digits.length === 4) result += '/'
  return result
}

function extractDigits(masked: string): string {
  return masked.replace(/\D/g, '').slice(0, 8)
}

export function DateInput({
  label = 'Date',
  value,
  onChange,
}: Omit<DateInputProps, 'onKeyDown'>) {
  const [open, setOpen] = useState(false)
  const [typing, setTyping] = useState(false)
  const [textValue, setTextValue] = useState('')
  const [calendarMonth, setCalendarMonth] = useState<Date | undefined>(
    undefined,
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const selectedDate = useMemo(() => {
    if (!value) return undefined
    const date = parseCalendarDate(value)
    return isValid(date) ? date : undefined
  }, [value])

  // Initialize calendar month from value on mount
  useEffect(() => {
    if (selectedDate) setCalendarMonth(selectedDate)
  }, [selectedDate])

  // Sync text from external value changes (e.g. calendar pick), but never
  // wipe a partial input the user is still working on.
  useEffect(() => {
    if (typing) return
    if (selectedDate) {
      setTextValue(format(selectedDate, DATE_FORMAT))
    }
  }, [selectedDate, typing])

  useEffect(() => {
    if (!open) return

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
        setTyping(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTyping(true)

    const digits = extractDigits(event.target.value)
    const masked = formatWithMask(digits)
    setTextValue(masked)

    if (digits.length === 0) {
      onChange('')
      return
    }

    if (digits.length === 8) {
      const parsed = parse(masked, DATE_FORMAT, new Date())
      if (isValid(parsed)) {
        onChange(toCalendarDate(parsed))
        setCalendarMonth(parsed)
      }
    }

    requestAnimationFrame(() => {
      const input = inputRef.current
      if (!input) return
      input.setSelectionRange(masked.length, masked.length)
    })
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false)
      setTyping(false)
      inputRef.current?.blur()
    }
  }

  const handleCalendarSelect = (date: Date | undefined) => {
    setTyping(false)
    if (date instanceof Date) {
      onChange(toCalendarDate(date))
      setOpen(false)
    } else {
      onChange('')
    }
  }

  return (
    <FilterInputWrapper label={label}>
      <div ref={containerRef} className="relative">
        <div className="relative">
          <Input
            ref={inputRef}
            placeholder={DATE_FORMAT.toLowerCase()}
            value={textValue}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setOpen(true)}
            className="pl-9"
            inputMode="numeric"
          />
          <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        </div>
        <span className="text-xs text-muted-foreground mt-1">mm/dd/yyyy</span>
        {open && (
          <div className="absolute bottom-full left-0 mb-1 z-50 rounded-md border bg-popover p-0 shadow-md animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleCalendarSelect}
              month={calendarMonth}
              defaultMonth={selectedDate}
            />
          </div>
        )}
      </div>
    </FilterInputWrapper>
  )
}
